import {
  Injectable,
  inject,
  signal,
  PLATFORM_ID,
  afterNextRender,
  computed,
  EnvironmentInjector,
  runInInjectionContext,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NgZone } from '@angular/core';
import { Router } from '@angular/router';
import {
  Auth as FirebaseAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  User as FirebaseUser,
  IdTokenResult,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  onAuthStateChanged,
  getIdToken,
  User,
  UserCredential,
  sendPasswordResetEmail,
} from '@angular/fire/auth';
import { BehaviorSubject, Observable, from, timer, EMPTY, throwError } from 'rxjs';
import { switchMap, map, catchError, tap, take, filter, shareReplay } from 'rxjs/operators';

import { environment } from '@environments/environment';

import { LoginCredentials, SessionData, FirebaseLoginUser } from '../models/auth.model';
import { Result } from '@app/core/models/common.model';
import { FirebaseError } from 'firebase/app';
import { FIREBASE_ERROR_CODES } from '@app/core/constants/codes/error-codes-firebase.constants';
import { FIREBASE_ERROR_MESSAGES } from '@app/core/constants/messages/error-messages-firebase.constants';

@Injectable({
  providedIn: 'root',
})
export class FirebaseAuthService {
  private fireAuth = inject(FirebaseAuth);
  private injector = inject(EnvironmentInjector);
  private platformId = inject(PLATFORM_ID);

  // Reactive state using signals - Used in Components
  public currentFbUser = signal<FirebaseUser | null>(null);

  /* Loading to take care of firebase delays - Used in Guards & Interceptors */
  private initializedSubject = new BehaviorSubject<boolean>(false);
  /** Emits once when the first onAuthStateChanged callback has completed */
  public ready$ = this.initializedSubject
    .asObservable()
    .pipe(filter(Boolean), take(1), shareReplay(1));

  private currentFbUserSubject = new BehaviorSubject<FirebaseUser | null>(null);
  public currentFbUser$ = this.currentFbUserSubject.asObservable().pipe(
    shareReplay(1) //cache the latest emission
  );

  // private sessionDataSubject = new BehaviorSubject<SessionData | null>(null);
  // public sessionData$ = this.sessionDataSubject.asObservable();

  private currentTokenSubject = new BehaviorSubject<string | null>(null);
  public currentToken$ = this.currentTokenSubject.asObservable();

  constructor(private router: Router, private ngZone: NgZone) {
    runInInjectionContext(this.injector, () => {
      onAuthStateChanged(this.fireAuth, (user: User | null) => {
        this.ngZone.run(async () => {
          await this.handleAuthStateChange(user);
          // mark hydration done after the first state arrives
          if (!this.initializedSubject.value) this.initializedSubject.next(true);
        });
      });
    });
  }
  //  private initializeAuth(): void {
  //     if (environment.consoleFlag)
  //     console.log("Inside..Initialize Auth...");
  //       onAuthStateChanged(this.fireAuth, (user: User | null) => {
  //       if (environment.consoleFlag)
  //         console.log('[AuthStateChanged] inside initializeAuth', user?.email || 'null');
  //          this.ngZone.run(() => {
  //         console.log('Auth state changed:', user);
  //         this.handleAuthStateChange(user);
  //   });
  //      });

  // }

  private async handleAuthStateChange(user: FirebaseUser | null): Promise<void> {
    if (environment.consoleFlag) console.log('inside handauthstate change..', user);
    //this.isLoadingSubject.next(true);
    if (user) {
      this.setFirebaseUser(user);
      try {
        // Get fresh token
        const tokenResult = await user.getIdTokenResult(true);
        if (tokenResult.token) {
          this.currentTokenSubject.next(tokenResult.token);
        }
      } catch (error) {
        console.error('Error handling auth state change:', error);
        this.logout();
      }
    } else {
      this.currentFbUser.set(null);
      this.currentFbUserSubject.next(null);
      this.currentTokenSubject.next(null);
    }
  }

  setFirebaseUser(user: User) {
    this.currentFbUser.set(user);
    this.currentFbUserSubject.next(user);
  }

  /**
   * Get the current Firebase user with a fresh ID token.
   */
  async getFirebaseUser(): Promise<{ user: FirebaseUser; token: string } | null> {
    const fbUser = this.currentFbUserSubject.value;

    if (!fbUser) {
      return null; // not logged in
    }

    try {
      // Force refresh of ID token
      const token = await fbUser.getIdToken(true);
      return { user: fbUser, token };
    } catch (err) {
      console.error(' Failed to refresh Firebase token:', err);
      return null;
    }
  }

  async forgotPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.fireAuth, email);
    } catch (error) {
      throw error;
    }
  }

  async getFreshToken(): Promise<string | null> {
    const fbUser = this.currentFbUserSubject.value;
    if (!fbUser) return null;

    try {
      return await fbUser.getIdToken(true); // force refresh
    } catch (err) {
      console.error(' Failed to refresh Firebase token:', err);
      return null;
    }
  }

  async getUid(): Promise<string | null> {
    const fbUser = this.currentFbUserSubject.value;
    if (!fbUser) return null;

    try {
      return await fbUser.uid; // force refresh
    } catch (err) {
      console.error(' Failed to get uid:', err);
      return null;
    }
  }

  /* logout method */
  async logout(): Promise<void> {
    try {
      this.fireAuth.signOut();
      this.currentFbUser.set(null);
      this.currentFbUserSubject.next(null);
    } catch (error) {
      console.error('Error during logout:', error); //todo: normalize error handling
      throw error; // let component decide
    }
  }

  //rollback Firebase User
  async rollbackFirebaseUser() {
    try {
      await this.fireAuth.currentUser?.delete();
      console.warn(
        `Rolled back Firebase user ${this.fireAuth.currentUser?.uid} due to backend failure.`
      );
    } catch (err) {
      console.error('Rollback failed:', err);
    }
  }
  // Firebase Login Methods
  //TODO: rewrite
  async loginWithEmail(
    email: string,
    password: string,
    rememberMe: boolean
  ): Promise<Result<FirebaseLoginUser>> {
    try {
      const result = await signInWithEmailAndPassword(this.fireAuth, email, password);
      const { uid, email: userEmail, providerData } = result.user;

      const loginUser: FirebaseLoginUser = {
        uid,
        token: await result.user.getIdToken(true), // ensure fresh token
        provider: providerData[0]?.providerId || 'email', //todo: fix
        registrationMethod: 'email',
        email: userEmail,
        rememberMe,
      };
      return { success: true, data: loginUser };
    } catch (error) {
      throw error;
    }
  }

  private isFirebaseError(error: any): error is { code: string; message: string } {
    return error && typeof error.code === 'string';
  }

  // Firebase Registration Methods
  async signUpWithEmail(email: string, password: string): Promise<UserCredential> {
    try {
      return await createUserWithEmailAndPassword(this.fireAuth, email, password);
    } catch (error) {
      throw error;
    }
  }

  /**
   * 🔹 Standardized Firebase Errors
   */
  public formatError(error: any): string {
    switch (error.code) {
      case FIREBASE_ERROR_CODES.INVALID_EMAIL:
        return FIREBASE_ERROR_MESSAGES.INVALID_EMAIL;
      case FIREBASE_ERROR_CODES.INVALID_CREDENTIALS:
        return FIREBASE_ERROR_MESSAGES.INVALID_CREDENTIALS;
      case FIREBASE_ERROR_CODES.INVALID_PASSWORD:
        return FIREBASE_ERROR_MESSAGES.INVALID_PASSWORD;
      case FIREBASE_ERROR_CODES.INVALID_LOGIN_CREDENTIALS:
        return FIREBASE_ERROR_MESSAGES.INVALID_LOGIN_CREDENTIALS;
      case FIREBASE_ERROR_CODES.INVALID_EMAIL_OR_PASSWORD:
        return FIREBASE_ERROR_MESSAGES.INVALID_EMAIL_OR_PASSWORD;
      case FIREBASE_ERROR_CODES.INVALID_EMAIL_OR_PASSWORD_CREDENTIALS:
        return FIREBASE_ERROR_MESSAGES.INVALID_EMAIL_OR_PASSWORD_CREDENTIALS;
      case FIREBASE_ERROR_CODES.WRONG_PASSWORD:
        return FIREBASE_ERROR_MESSAGES.WRONG_PASSWORD;
      case FIREBASE_ERROR_CODES.USER_ALREADY_EXISTS:
        return FIREBASE_ERROR_MESSAGES.USER_ALREADY_EXISTS;
      case FIREBASE_ERROR_CODES.WEAK_PASSWORD:
        return FIREBASE_ERROR_MESSAGES.WEAK_PASSWORD;
      case FIREBASE_ERROR_CODES.POPUP_CLOSED_BY_USER:
        return FIREBASE_ERROR_MESSAGES.POPUP_CLOSED_BY_USER;
      case FIREBASE_ERROR_CODES.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL:
        return FIREBASE_ERROR_MESSAGES.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL;
      case FIREBASE_ERROR_CODES.NETWORK_REQUEST_FAILED:
        return FIREBASE_ERROR_MESSAGES.NETWORK_REQUEST_FAILED;
      case FIREBASE_ERROR_CODES.USER_DISABLED:
        return FIREBASE_ERROR_MESSAGES.USER_DISABLED;
      case FIREBASE_ERROR_CODES.POPUP_BLOCKED:
        return FIREBASE_ERROR_MESSAGES.POPUP_BLOCKED;
      default:
        return error.message || FIREBASE_ERROR_MESSAGES.UNKNOWN_ERROR;
    }
  }
}
